import { I18n } from "@assets/strings";
import React, { JSX } from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import defaultThumbnail from "@assets/images/default-thumbnail.jpg";
import { Path } from "@core/routing/path";
import { Category } from "@domain/lms/category/category";
import { Course } from "@domain/lms/course/course";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { ImageVersions } from "@domain/media/dtos";
import { useHover, useIsMobile } from "@utils/hooks";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CourseDetails } from "../../components/course/courseDetails";
import { theme } from "@ui/styles/theme";
import { alpha, gap, mobile } from "@ui/styles/utils";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { BadgeLabel } from "@ui/components/badgeLabel";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { CourseIcon } from "@ui/components/icons";
import { Row, row } from "@ui/components/layout";
import { UserView } from "@ui/components/profile/userView";
import { SubAuthor } from "@ui/components/subAuthor";
import { CourseProviderIcon } from "./courseProviderIcon";
import { CourseStatusLabel } from "./courseStatusLabel";
import { CourseThumbnailMenu } from "./courseThumbnailMenu";

interface CourseThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	course: Course;
	displayStatus?: boolean;
	displayAuthorAsMe?: boolean;
}

const renderCategorieLabel = (small: boolean, category: Category): JSX.Element => {
	if (small)
		return (
			<StyledCategoryLabel
				category={category}
				isSmall={small}
				renderIcons={() => <CourseIcon color={theme.colors.white} />}
				isTitleTruncated={true}
			/>
		);
	return <StyledCategoryLabel category={category} />;
};

export const CourseThumbnail: React.FC<CourseThumbnailProps> = ({
	course,
	displayStatus,
	displayAuthorAsMe,
	...htmlProps
}) => {
	const { category, banner, provider, author } = course;
	const displayedAuthor = getDisplayedAuthor(course);
	const subAuthor = displayedAuthor !== author ? author : undefined;
	const { ref, isHovered } = useHover();
	const isMobile = useIsMobile();
	const small = isMobile;

	return (
		<Container {...htmlProps} ref={ref}>
			<Content>
				<Link to={Path.Lms.ReferenceCourse(course)}>
					<StyledBackgroundImage
						isHovered={isHovered}
						image={banner}
						defaultUrl={!banner ? defaultThumbnail : undefined}
						size={ImageVersions.MEDIUM}
						backdrop
					/>
				</Link>
				<Infos>
					<Row gap={10} align="center">
						<StyledCourseDetails course={course} hideLanguage />
						<CourseProviderIcon provider={provider} />
					</Row>
					<CourseTitle>{course.title}</CourseTitle>
					<BottomInfos>
						<StyledUserView
							user={displayedAuthor}
							alignStart={!!subAuthor}
							renderBottomAccessory={
								subAuthor ? () => <SubAuthor author={subAuthor} displayAuthorAsMe={displayAuthorAsMe} /> : undefined
							}
						/>
						{displayStatus && <CourseStatusLabel status={course.status} />}
					</BottomInfos>
				</Infos>
			</Content>
			{category && category !== "OTHERS" && renderCategorieLabel(small, category)}
			<StyledMenu course={course} />

			{!isMobile && (
				<Badge
					renderIcons={() => <CourseIcon />}
					isHovered={isHovered}
					badgeTitle={I18n.t("course.thumbnail.badge_label")}
					hoverWidth={130}
				/>
			)}
		</Container>
	);
};

const Container = styled.div`
	position: relative;
`;

const StyledMenu = styled(CourseThumbnailMenu)`
	position: absolute;
	right: 24px;
	top: 24px;
	background-color: ${({ theme }) => alpha(theme.colors.backgroundPrimary, 0.98)};

	@media (max-width: 900px) {
		right: 10px;
		top: 10px;
	}
`;

const Content = styled(RoundedBlock)`
	max-height: 342px;
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	border-radius: 13px;
	overflow: hidden;

	> a {
		width: 100%;
		height: 100%;
	}

	@media (max-width: 900px) {
		padding-top: 0;
	}
`;

const Infos = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	pointer-events: none;
	padding: 24px;
	${gap(10)};
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-end;
	color: ${({ theme }) => theme.colors.white};

	@media (max-width: 900px) {
		padding: 10px;
	}
`;

const StyledUserView = styled(UserView)`
	color: ${({ theme }) => theme.colors.white};
`;

const CourseTitle = styled.div`
	${({ theme }) => theme.textStyles.desktopSubtitle};
	color: ${({ theme }) => theme.colors.white};
	min-width: 270px;
	width: 90%;
	display: -webkit-box;
	overflow: hidden;
	text-overflow: ellipsis;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
`;

const StyledBackgroundImage = styled(BackgroundImage)`
	height: 342px;
	@media ${mobile} {
		height: 235px;
	}
`;

const StyledCourseDetails = styled(CourseDetails)`
	color: ${({ theme }) => theme.colors.white};

	svg path:not(.no-fill) {
		fill: ${({ theme }) => theme.colors.white};
	}
`;

const StyledCategoryLabel = styled(CategoryLabel)<{ isSmall?: boolean }>`
	position: absolute;
	${({ isSmall }) =>
		isSmall
			? css`
					left: 20px;
					top: 10px;
				`
			: css`
					right: 64px;
					top: 24px;
				`};
`;

const Badge = styled(BadgeLabel)`
	position: absolute;
	left: 20px;
	top: 1px;

	@media (max-width: 900px) {
		left: 10px;
		top: 0px;
	}
`;

const BottomInfos = styled.div`
	align-self: stretch;
	${row(10, "space-between")};
`;
